import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getDocument } from '@atlaskit/browser-apis';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { RouteContext } from '@atlassian/react-resource-router';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useEffectWithoutConcurrency } from '@jira/platform__strict-mode/src/useEffectWithoutConcurrency.tsx';
import { getNewEditorLink } from '@jira/project-settings__project-configuration-links/src/getNewEditorLink.tsx';
import { getUserWorkflowEditorPreference } from '@jira/project-settings__workflow-editor-preference-settings/src/getUserWorkflowEditorPreference.tsx';
import { WorkflowEditorPreferenceDropdown } from '@jira/project-settings__workflow-editor-preference-settings/src/WorkflowEditorPreferenceDropdown.tsx';
import {
	LEGACY_EDITOR,
	NEW_EDITOR,
	type EditorType,
} from '@jira/project-settings__workflow-editor-preference-settings/src/WorkflowPublicationStatus.types.tsx';
import { LegacyPage } from '@jira/router-routes__common/src/ui/legacy-page/main.tsx';
import { useProvideFeedbackDialog } from './useProvideFeedbackDialog.tsx';

type Props = RouteContext;

const BLACKLISTED_PATHS_REGEX = [
	// This blacklist any URL within the same Legacy workflow editor containing `/secure/admin/workflows/*`
	'^/secure/admin/workflows/.*',
	// This blacklist redirects from the new workflow editor to the legacy workflow editor containing `/jira/settings/issues/workflows/<anyId>`
	'^/jira/settings/issues/workflows/[^/].*',
];

export const hasNavigatedViaDefault = (referrer: string, hostname: string): boolean => {
	if (referrer) {
		const referrerUrl = new URL(referrer);
		if (hostname === referrerUrl.hostname) {
			const referrerPath = referrerUrl.pathname;

			// Check if the referrer path matches any of the blacklisted paths
			const isBlacklisted = BLACKLISTED_PATHS_REGEX.some((pathRegex) => {
				const regex = new RegExp(pathRegex);
				return regex.test(referrerPath);
			});

			return !isBlacklisted;
		}
	}
	return false;
};

export const LegacyWorkflowDesigner = (props: Props): React.JSX.Element => {
	const elm = __SERVER__
		? null
		: globalThis.document?.getElementById('workflow-editor-preference-dropdown');
	const workflowId = elm?.getAttribute('data-workflow-id') ?? '';
	const workflowName = elm?.getAttribute('data-workflow-name') ?? '';
	const editMode = elm?.getAttribute('data-edit-mode') ?? undefined;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { renderFeedbackDialog } = useProvideFeedbackDialog(props);

	useEffect(() => {
		const referrer = globalThis.document?.referrer || '';
		if (hasNavigatedViaDefault(referrer, globalThis.window.location.hostname)) {
			getUserWorkflowEditorPreference().then((preference: EditorType | null) => {
				const defaultEditorPreference =
					preference !== NEW_EDITOR && preference !== LEGACY_EDITOR ? 'NONE' : preference;

				fireTrackAnalytics(createAnalyticsEvent({}), 'defaultWorkflowEditor applied', {
					defaultEditor: LEGACY_EDITOR,
					defaultEditorPreference,
				});
			});
		}
	}, [createAnalyticsEvent]);

	useEffectWithoutConcurrency(() => {
		const document = getDocument();
		const provideFeedbackElement = document?.getElementById(
			'legacy-workflow-deprecation-banner-provide-feedback-action',
		);

		const switchToNewEditorElement = document?.getElementById(
			'legacy-workflow-deprecation-banner-switch-editor-action',
		);
		if (provideFeedbackElement) {
			provideFeedbackElement.addEventListener('click', (event: Event) => {
				event.preventDefault();
				renderFeedbackDialog();
			});
		}
		if (switchToNewEditorElement) {
			switchToNewEditorElement.addEventListener('click', (event: Event) => {
				event.preventDefault();

				if (workflowId) {
					globalThis.window.location.assign(getNewEditorLink(workflowId));
				}
			});
		}
	});

	return (
		<JSErrorBoundary
			id="jsm-dropdown-error-boundary"
			packageName="jiraRouterRoutesLegacyWorkflowDesignerEntries"
			teamName="jsd-shield"
			fallback="flag"
		>
			<LegacyPage {...props} />
			{elm &&
				createPortal(
					<WorkflowEditorPreferenceDropdown
						workflowId={workflowId}
						workflowName={workflowName}
						// oxlint-disable-next-line typescript/consistent-type-assertions
						editMode={editMode as 'draft' | 'live'}
					/>,
					elm,
				)}
		</JSErrorBoundary>
	);
};
